import { BRAND } from "../risikoanalyseStep/tokens";
import type { ContractStepKind } from "./types";

const W = 140;
const H = 96;

const Wrap = ({ children }: { children: React.ReactNode }) => (
  <svg width={W} height={H} viewBox="0 0 140 96" fill="none">
    {children}
  </svg>
);

const TarifierungHero = () => (
  <Wrap>
    {/* Stack of 4 contract cards offset */}
    {[0, 6, 12, 18].map((y, i) => (
      <rect
        key={i}
        x={20 + i * 4}
        y={14 + y}
        width="78"
        height="46"
        rx="4"
        fill={i === 3 ? BRAND.gold : BRAND.white}
        stroke={BRAND.purple}
        strokeWidth="1.5"
      />
    ))}
    <path d="M44 50h36M44 58h28M44 66h20" stroke={BRAND.purple} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="116" cy="38" r="14" fill={BRAND.pillarOrange} />
    <path d="M110 38l4 4 8-8" stroke={BRAND.white} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </Wrap>
);

const RisikoInfoHero = () => (
  <Wrap>
    {/* Vehicle silhouette + clipboard with checkmarks */}
    <g transform="translate(8 30)">
      <path
        d="M2 28l4-12a4 4 0 0 1 3.6-3h26a4 4 0 0 1 3.6 3l4 12"
        fill={BRAND.bg}
        stroke={BRAND.purple}
        strokeWidth="1.6"
      />
      <path d="M0 28h46v8H0z" fill={BRAND.purple} />
      <circle cx="11" cy="36" r="3" fill={BRAND.gold} />
      <circle cx="35" cy="36" r="3" fill={BRAND.gold} />
    </g>
    <g transform="translate(70 14)">
      <rect width="56" height="68" rx="4" fill={BRAND.white} stroke={BRAND.purple} strokeWidth="1.6" />
      <rect x="16" y="-4" width="24" height="10" rx="2" fill={BRAND.purple} />
      {[0, 1, 2, 3].map((i) => (
        <g key={i} transform={`translate(8 ${14 + i * 12})`}>
          <rect width="8" height="8" rx="1.5" fill={BRAND.gold} />
          <path d="M2 4l2 2 4-4" stroke={BRAND.white} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 4h28" stroke={BRAND.muted} strokeWidth="1.4" strokeLinecap="round" />
        </g>
      ))}
    </g>
  </Wrap>
);

const GefahrenHero = () => (
  <Wrap>
    {/* Shield with car silhouette inside + lightning + drop accents */}
    <path
      d="M70 12l40 8v22c0 18-16 30-40 36-24-6-40-18-40-36V20z"
      fill={BRAND.pillarBlue}
      stroke={BRAND.purple}
      strokeWidth="1.6"
    />
    <path
      d="M70 18l30 6v18c0 14-12 24-30 28-18-4-30-14-30-28V24z"
      fill={BRAND.white}
      stroke={BRAND.purple}
      strokeWidth="1.4"
    />
    <g transform="translate(48 44)">
      <path
        d="M2 14l3-9a3 3 0 0 1 2.7-2h26a3 3 0 0 1 2.7 2l3 9z"
        fill={BRAND.gold}
        stroke={BRAND.ink}
        strokeWidth="1.2"
      />
      <path d="M0 14h44v6H0z" fill={BRAND.ink} />
      <circle cx="9" cy="20" r="2" fill={BRAND.bg} />
      <circle cx="35" cy="20" r="2" fill={BRAND.bg} />
    </g>
    <path
      d="M22 10l-6 14h6l-3 12 10-16h-6l4-10z"
      fill={BRAND.gold}
      stroke={BRAND.ink}
      strokeWidth="1.2"
    />
  </Wrap>
);

const VertragsInfoHero = () => (
  <Wrap>
    {/* Document with stamp and €-coins */}
    <rect x="14" y="10" width="76" height="74" rx="4" fill={BRAND.white} stroke={BRAND.purple} strokeWidth="1.6" />
    <path d="M22 24h60M22 34h60M22 44h44M22 54h36M22 64h28" stroke={BRAND.muted} strokeWidth="1.4" strokeLinecap="round" />
    <g transform="translate(60 56)">
      <circle cx="14" cy="14" r="14" fill={BRAND.pillarOrange} stroke={BRAND.ink} strokeWidth="1.4" />
      <text x="14" y="18" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="800" fill={BRAND.white}>
        OK
      </text>
    </g>
    <g transform="translate(96 14)">
      <circle cx="20" cy="20" r="18" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.4" />
      <text x="20" y="26" textAnchor="middle" fontFamily="Arial" fontSize="20" fontWeight="800" fill={BRAND.ink}>
        €
      </text>
    </g>
    <g transform="translate(96 50)">
      <circle cx="18" cy="18" r="14" fill={BRAND.pillarBlue} stroke={BRAND.ink} strokeWidth="1.4" />
      <text x="18" y="23" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="800" fill={BRAND.white}>
        %
      </text>
    </g>
  </Wrap>
);

const SummaryHero = () => (
  <Wrap>
    <rect x="14" y="14" width="112" height="68" rx="4" fill={BRAND.white} stroke={BRAND.purple} strokeWidth="1.6" />
    <path d="M22 28h60M22 38h80M22 48h60M22 58h70M22 68h44" stroke={BRAND.muted} strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="118" cy="34" r="10" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.4" />
    <path d="M114 34l3 3 5-6" stroke={BRAND.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Wrap>
);

const KontaktHero = () => (
  <Wrap>
    {/* Person silhouette + ID card */}
    <circle cx="44" cy="38" r="14" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.6" />
    <path d="M28 78c0-9 7-16 16-16s16 7 16 16" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.6" />
    <rect x="74" y="22" width="54" height="50" rx="4" fill={BRAND.white} stroke={BRAND.purple} strokeWidth="1.6" />
    <circle cx="86" cy="38" r="6" fill={BRAND.pillarOrange} />
    <path d="M98 36h22M98 42h18" stroke={BRAND.muted} strokeWidth="1.4" strokeLinecap="round" />
    <path d="M82 56h36M82 62h26" stroke={BRAND.muted} strokeWidth="1.4" strokeLinecap="round" />
  </Wrap>
);

const VertragsBasisHero = () => (
  <Wrap>
    {/* Two contracts (old + new) with handshake arrow */}
    <rect x="10" y="18" width="50" height="58" rx="4" fill={BRAND.white} stroke={BRAND.purple} strokeWidth="1.6" />
    <path d="M18 32h34M18 42h28M18 52h22" stroke={BRAND.muted} strokeWidth="1.4" strokeLinecap="round" />
    <rect x="80" y="18" width="50" height="58" rx="4" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.6" />
    <path d="M88 32h34M88 42h28M88 52h22" stroke={BRAND.ink} strokeWidth="1.4" strokeLinecap="round" />
    <path d="M64 48l12 0M72 44l4 4-4 4" stroke={BRAND.pillarOrange} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </Wrap>
);

const RisikoAuswahlHero = () => (
  <Wrap>
    {/* Two vehicle silhouettes with a check */}
    <g transform="translate(8 30)">
      <path d="M2 24l3-10a3 3 0 0 1 2.7-2h26a3 3 0 0 1 2.7 2l3 10z" fill={BRAND.bg} stroke={BRAND.purple} strokeWidth="1.5" />
      <path d="M0 24h44v6H0z" fill={BRAND.purple} />
      <circle cx="10" cy="30" r="2.6" fill={BRAND.gold} />
      <circle cx="34" cy="30" r="2.6" fill={BRAND.gold} />
    </g>
    <g transform="translate(76 14)">
      <path d="M2 24l3-10a3 3 0 0 1 2.7-2h26a3 3 0 0 1 2.7 2l3 10z" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.5" />
      <path d="M0 24h44v6H0z" fill={BRAND.ink} />
      <circle cx="10" cy="30" r="2.6" fill={BRAND.bg} />
      <circle cx="34" cy="30" r="2.6" fill={BRAND.bg} />
    </g>
    <circle cx="120" cy="20" r="10" fill={BRAND.pillarOrange} />
    <path d="M115 20l3 3 6-6" stroke={BRAND.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Wrap>
);

const ZahlungHero = () => (
  <Wrap>
    {/* Bank-card + euro coin */}
    <rect x="14" y="22" width="80" height="48" rx="6" fill={BRAND.purple} stroke={BRAND.ink} strokeWidth="1.5" />
    <rect x="14" y="32" width="80" height="10" fill={BRAND.ink} />
    <rect x="22" y="52" width="30" height="6" rx="2" fill={BRAND.gold} />
    <circle cx="116" cy="48" r="16" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1.6" />
    <text x="116" y="54" textAnchor="middle" fontFamily="Arial" fontSize="20" fontWeight="800" fill={BRAND.ink}>€</text>
  </Wrap>
);

const VertragsmaskeHero = () => (
  <Wrap>
    {/* Document matrix with multiple rows / columns */}
    <rect x="10" y="14" width="120" height="68" rx="6" fill={BRAND.white} stroke={BRAND.purple} strokeWidth="1.6" />
    <path d="M10 30h120" stroke={BRAND.purple} strokeWidth="1.4" />
    <path d="M40 14v68M80 14v68" stroke={BRAND.purple} strokeWidth="1.2" opacity="0.55" />
    {[0, 1, 2, 3].map((row) => (
      <g key={row}>
        <circle cx="24" cy={42 + row * 10} r="2.4" fill={BRAND.gold} />
        <path d={`M50 ${42 + row * 10}h26`} stroke={BRAND.muted} strokeWidth="1.4" strokeLinecap="round" />
        <path d={`M90 ${42 + row * 10}h32`} stroke={BRAND.muted} strokeWidth="1.4" strokeLinecap="round" />
      </g>
    ))}
    <circle cx="112" cy="22" r="6" fill={BRAND.pillarOrange} />
    <path d="M109 22l2 2 4-4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </Wrap>
);

const Heroes: Partial<Record<ContractStepKind, () => JSX.Element>> = {
  kontakt: KontaktHero,
  vertragsBasis: VertragsBasisHero,
  tarifierung: TarifierungHero,
  risikoAuswahl: RisikoAuswahlHero,
  risikoInfo: RisikoInfoHero,
  summen: VertragsInfoHero,
  gefahren: GefahrenHero,
  zahlung: ZahlungHero,
  vertragsInfo: VertragsInfoHero,
  summary: SummaryHero,
};

const StepHero = ({ kind }: { kind: ContractStepKind }) => {
  const Cmp = Heroes[kind] ?? RisikoInfoHero;
  return (
    <div style={{ width: W, height: H, display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
      <Cmp />
    </div>
  );
};

export default StepHero;
